import style from './power-flow-3-sources.module.scss';
import { cn } from '@/utils/utils.ts';

interface Measurements {
  production?: number;
  grid?: number;
  consumption?: number;
  batteryPower?: number;
  batterySoC?: number;
}

interface PlantPowerFlowProps {
  className?: string;
  width?: string;
  height?: string;
  measurements: Measurements;
}

export const PlantPowerFlow3Sources = ({
  className,
  width,
  height,
  measurements,
}: PlantPowerFlowProps) => {
  const gridPlantFlow = getGridPlantFlow(measurements);
  const batteryGridFlow = getBatteryGridFlow(measurements);
  const batteryPlantFlow = getBatteryPlantFlow(measurements);

  return (
    <svg
      className={cn('max-w-[26rem]', className)}
      width={width}
      height={height}
      viewBox="0 0 307 218"
      fill="none"
    >
      <g id="PowerFlow">
        <g id="Frame">
          <g id="Battery">
            <g id="Circle">
              <path
                id="Vector"
                d="M173 117H134C126.82 117 121 122.82 121 130V184C121 191.18 126.82 197 134 197H173C180.18 197 186 191.18 186 184V130C186 122.82 180.18 117 173 117Z"
                fill="#F9FAFB"
              />
              <path
                id="Vector_2"
                d="M173 117H134C126.82 117 121 122.82 121 130V184C121 191.18 126.82 197 134 197H173C180.18 197 186 191.18 186 184V130C186 122.82 180.18 117 173 117Z"
                stroke="#A75161"
                strokeWidth="2"
              />
              <g id="Icon">
                <path
                  id="Vector_3"
                  d="M150.176 135.015V141.757C150.176 141.854 150.206 141.939 150.269 142.017C150.328 142.093 150.414 142.153 150.51 142.188C150.578 142.212 150.648 142.224 150.724 142.224H156.275C156.389 142.224 156.489 142.198 156.581 142.145C156.67 142.094 156.741 142.021 156.782 141.939C156.81 141.881 156.824 141.822 156.824 141.757V134.939C153.962 135.944 152.613 134.239 150.176 135.015ZM154.935 139.914V140.542H152.065V139.914H154.935Z"
                  fill="black"
                />
                <path
                  id="Vector_2_2"
                  d="M152.065 130.109V130.737H153.131V131.644H153.869V130.737H154.935V130.109H153.869V129.203H153.131V130.109H152.065Z"
                  fill="black"
                />
                <path
                  id="Vector_3_2"
                  d="M151.559 125.46V125.782C151.559 125.998 151.514 126.253 150.618 126.53C150.213 126.541 149.824 126.615 149.469 126.743C148.889 126.952 148.398 127.299 148.051 127.736C147.703 128.173 147.5 128.704 147.5 129.27V133.027V138V141.757C147.5 142.135 147.59 142.497 147.754 142.825C147.999 143.318 148.407 143.736 148.921 144.031C149.434 144.327 150.059 144.5 150.724 144.5H156.276C156.719 144.5 157.145 144.423 157.531 144.284C158.111 144.076 158.601 143.729 158.949 143.291C159.296 142.854 159.5 142.323 159.5 141.757V138V133.027V129.27C159.5 128.893 159.41 128.531 159.246 128.202C159.001 127.709 158.593 127.292 158.079 126.996C157.592 126.716 157.006 126.546 156.379 126.529C155.486 126.252 155.441 125.998 155.441 125.782V125.46C155.441 124.93 154.936 124.5 154.313 124.5H152.687C152.064 124.5 151.559 124.93 151.559 125.46ZM156.276 127.849C156.508 127.849 156.727 127.889 156.926 127.96C157.225 128.068 157.481 128.249 157.661 128.476C157.842 128.704 157.946 128.974 157.946 129.27V132.396V133.027V134.348V136.679V138V139.099V141.757C157.946 141.956 157.9 142.141 157.815 142.31C157.689 142.565 157.476 142.783 157.209 142.936C156.942 143.089 156.624 143.178 156.276 143.178H150.724C150.491 143.178 150.273 143.139 150.074 143.067C149.775 142.96 149.519 142.778 149.339 142.552C149.158 142.324 149.054 142.054 149.053 141.757V139.099V138V136.679V134.348V133.027V132.396V129.27C149.054 129.072 149.1 128.887 149.184 128.717C149.311 128.463 149.524 128.245 149.791 128.091C150.058 127.938 150.375 127.849 150.724 127.849H156.276Z"
                  fill="black"
                />
              </g>
              <g id="Text_SoC">
                <text
                  id="BatteryText_SoC"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="14"
                  letterSpacing="0em"
                >
                  {Number(measurements?.batterySoC) === 0 || !measurements?.batterySoC ? (
                    <tspan x="143.5" y="158.182">
                      {formatMeasurement(measurements?.batterySoC, 0)} %
                    </tspan>
                  ) : (
                    <tspan x="128.256" y="158.182">
                      {formatMeasurement(measurements?.batterySoC, 7)} %
                    </tspan>
                  )}
                </text>
              </g>
              <g id="Text">
                <text
                  id="BatteryText"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="14"
                  letterSpacing="0em"
                >
                  {Number(measurements?.batteryPower) === 0 || !measurements?.batteryPower ? (
                    <tspan x="148.5" y="174.182">
                      {formatMeasurement(measurements?.batteryPower, 0)}
                    </tspan>
                  ) : (
                    <tspan x="130" y="174.182">
                      {formatMeasurement(measurements?.batteryPower, 7)}
                    </tspan>
                  )}
                </text>
                <text
                  id="kW"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="10"
                  letterSpacing="0em"
                >
                  <tspan x="145.829" y="187.772">
                    kW
                  </tspan>
                </text>
              </g>
            </g>
            <text
              id="Battery_2"
              fill="black"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontSize="14"
              fontWeight="500"
              letterSpacing="0em"
            >
              <tspan x="130" y="210.182">
                Battery
              </tspan>
            </text>
          </g>
          <g id="Grid">
            <g id="Circle_3">
              <path
                id="Vector_4"
                d="M56 4H17C9.8203 4 4 9.8203 4 17V56C4 63.1797 9.8203 69 17 69H56C63.1797 69 69 63.1797 69 56V17C69 9.8203 63.1797 4 56 4Z"
                fill="#F9FAFB"
              />
              <path
                id="Vector_5"
                d="M56 4H17C9.8203 4 4 9.8203 4 17V56C4 63.1797 9.8203 69 17 69H56C63.1797 69 69 63.1797 69 56V17C69 9.8203 63.1797 4 56 4Z"
                stroke="#005780"
                strokeWidth="2"
              />
              <g id="Icon_3">
                <path
                  id="Vector_4_2"
                  d="M42.266 20.256C42.5634 20.256 42.8045 20.014 42.8045 19.717V19.111H42.9615C43.2589 19.111 43.5 18.87 43.5 18.572C43.5 18.275 43.2589 18.034 42.9615 18.034H39.3045V16.418H40.3814V17.024C40.3814 17.321 40.6225 17.562 40.9199 17.562C41.2173 17.562 41.4584 17.321 41.4584 17.024V16.418H41.6154C41.9128 16.418 42.1538 16.176 42.1538 15.879C42.1538 15.581 41.9128 15.34 41.6154 15.34H39.0944L36.9648 11.765C36.9413 11.726 36.8157 11.6 36.7759 11.576C36.5203 11.424 36.19 11.507 36.0377 11.763L33.9056 15.34H31.3846C31.0873 15.34 30.8462 15.581 30.8462 15.879C30.8462 16.176 31.0873 16.418 31.3846 16.418H31.4968V17.024C31.4968 17.321 31.7379 17.562 32.0353 17.562C32.3326 17.562 32.5737 17.321 32.5737 17.024V16.418H33.6507V18.034H30.0385C29.7411 18.034 29.5 18.275 29.5 18.572C29.5 18.87 29.7411 19.111 30.0385 19.111H30.1507V19.717C30.1507 20.014 30.3918 20.255 30.6891 20.255C30.9865 20.255 31.2276 20.014 31.2276 19.717V19.111H33.6507V20.986C33.6507 21.01 33.6507 21.033 33.6507 21.056V23.526L31.5808 29.175C30.5316 29.798 29.9712 30.607 29.9712 31.5H43.0288C43.0288 30.611 42.4517 29.807 41.412 29.187L39.3045 23.526V19.111H41.7276V19.717C41.7276 20.014 41.9687 20.256 42.266 20.256ZM36.5 28.025C35.2265 28.025 34.0063 28.214 32.966 28.561L33.4199 27.329L36.5032 25.563L39.5886 27.334L40.0454 28.567C39.0026 28.217 37.7782 28.025 36.5 28.025ZM34.7276 21.949L37.6858 23.638L36.4869 24.321L34.7276 23.315V21.949ZM34.495 24.411L35.4205 24.942L33.9995 25.756L34.495 24.411ZM37.5863 24.943L38.5077 24.415L39.0045 25.756L37.5863 24.943ZM35.3011 21.036L38.2276 19.475V22.713L35.3011 21.036ZM38.2276 17.649L36.1684 16.418H38.2276V17.649ZM36.5005 13.09L37.8408 15.34H35.1595L36.5005 13.09ZM34.7276 16.83L36.7846 18.034H34.7276V16.83ZM36.6442 19.111L34.7275 20.121V19.111H36.6442Z"
                  fill="black"
                />
              </g>
              <g id="Frame 156_2">
                <text
                  id="GridText"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="14"
                  letterSpacing="0em"
                >
                  {Number(measurements?.grid) === 0 || !measurements?.grid ? (
                    <tspan x="32.3" y="45.182">
                      {formatMeasurement(measurements?.grid, 0)}
                    </tspan>
                  ) : (
                    <tspan x="13.5" y="45.182">
                      {formatMeasurement(measurements?.grid, 7)}
                    </tspan>
                  )}
                </text>
                <text
                  id="kW_3"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="10"
                  letterSpacing="0em"
                >
                  <tspan x="28.8291" y="58.7724">
                    kW
                  </tspan>
                </text>
              </g>
            </g>
            <text
              id="Grid_2"
              fill="black"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontSize="14"
              fontWeight="500"
              letterSpacing="0em"
            >
              <tspan x="22.2061" y="82.682">
                Grid
              </tspan>
            </text>
          </g>
          <g id="Plant">
            <g id="Circle_4">
              <path
                id="Vector_6"
                d="M290 4H251C243.82 4 238 9.8203 238 17V56C238 63.1797 243.82 69 251 69H290C297.18 69 303 63.1797 303 56V17C303 9.8203 297.18 4 290 4Z"
                fill="#F9FAFB"
              />
              <path
                id="Vector_7"
                d="M290 4H251C243.82 4 238 9.8203 238 17V56C238 63.1797 243.82 69 251 69H290C297.18 69 303 63.1797 303 56V17C303 9.8203 297.18 4 290 4Z"
                stroke="#58ADA1"
                strokeWidth="2"
              />
              <g id="Icon_4">
                <path
                  id="Vector_5_2"
                  d="M268.64 24.3291C268.258 24.3291 267.942 24.6661 267.942 25.0711V26.5551C267.942 26.9611 268.258 27.2971 268.64 27.2971C269.021 27.2971 269.337 26.9611 269.337 26.5551V25.0711C269.337 24.6661 269.021 24.3291 268.64 24.3291Z"
                  fill="#292D32"
                />
                <path
                  id="Vector_6_2"
                  d="M279.802 30.016H278.872V18.867C278.872 18.254 278.612 17.68 278.156 17.304L271.644 11.923C270.974 11.359 270.026 11.359 269.356 11.923L262.844 17.304C262.388 17.68 262.128 18.254 262.128 18.857L262.081 30.016H261.198C260.816 30.016 260.5 30.343 260.5 30.758C260.5 31.164 260.816 31.5 261.198 31.5H279.802C280.184 31.5 280.5 31.164 280.5 30.758C280.5 30.343 280.184 30.016 279.802 30.016ZM269.105 15.672H271.895C272.277 15.672 272.593 16.008 272.593 16.414C272.593 16.82 272.277 17.156 271.895 17.156H269.105C268.723 17.156 268.407 16.82 268.407 16.414C268.407 16.008 268.723 15.672 269.105 15.672ZM275.151 30.016H265.849V21.36C265.849 20.539 266.472 19.876 267.244 19.876H273.756C274.528 19.876 275.151 20.539 275.151 21.36V30.016Z"
                  fill="black"
                />
              </g>
              <g id="Frame 156_3">
                <text
                  id="PlantText"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="14"
                  letterSpacing="0em"
                >
                  {Number(measurements?.consumption) === 0 || !measurements?.consumption ? (
                    <tspan x="266.256" y="45.182">
                      {formatMeasurement(measurements?.consumption, 0)}
                    </tspan>
                  ) : (
                    <tspan x="247.256" y="45.182">
                      {formatMeasurement(measurements?.consumption, 6)}
                    </tspan>
                  )}
                </text>
                <text
                  id="kW_4"
                  fill="#595959"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="10"
                  letterSpacing="0em"
                >
                  <tspan x="262.829" y="58.7724">
                    kW
                  </tspan>
                </text>
              </g>
            </g>
            <text
              id="Plant_2"
              fill="black"
              xmlSpace="preserve"
              style={{ whiteSpace: 'pre' }}
              fontSize="14"
              fontWeight="500"
              letterSpacing="0em"
            >
              <tspan x="253.226" y="82.682">
                Plant
              </tspan>
            </text>
          </g>
          <g id="EnergyFlow">
            <g id="grid-battery">
              <path
                id="grid-battery-line"
                d="M70 49H101C123.091 49 141 66.909 141 89V116"
                stroke={batteryGridFlow?.display ? '#A75161' : '#9ca3af'}
              />
              {batteryGridFlow?.display && (
                <circle
                  className={cn(style.grid_battery_circle, batteryGridFlow?.directionStyle)}
                  id="grid-battery-cicle"
                  r="3.5"
                  fill="#A75161"
                />
              )}
            </g>
            <g id="battery-plant">
              <path
                id="battery-plant-line"
                d="M237 49H206C183.909 49 166 66.909 166 89V116"
                stroke={batteryPlantFlow?.display ? '#58ADA1' : '#9ca3af'}
              />
              {batteryPlantFlow?.display && (
                <circle
                  className={cn(style.battery_plant_circle, batteryPlantFlow?.directionStyle)}
                  id="battery-plant-circle"
                  r="3.5"
                  fill="#58ADA1"
                />
              )}
            </g>
            <g id="grid-plant">
              <path
                id="grid-plant-line"
                d="M70 37L237 37"
                stroke={gridPlantFlow?.display ? 'black' : '#9ca3af'}
              />
              {gridPlantFlow?.display && (
                <circle
                  className={cn(style.grid_plant_circle, gridPlantFlow?.directionStyle)}
                  id="grid-plant-circle"
                  r="3.5"
                  fill="black"
                />
              )}
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
};

function isValidNumber(value: number | undefined): value is number {
  return value !== undefined && !isNaN(value);
}

function getGridPlantFlow({ consumption, grid }: Measurements) {
  if (!isValidNumber(consumption) || !isValidNumber(grid)) return;

  if (consumption === 0 || grid === 0) return;

  if (grid < 0 || consumption < 0) return;

  return {
    display: true,
    directionStyle: style.forward_flow,
  };
}

function getBatteryPlantFlow({ consumption, batteryPower }: Measurements) {
  if (!isValidNumber(consumption) || !isValidNumber(batteryPower)) return;

  if (batteryPower === 0 || consumption === 0) return;

  if (batteryPower < 0 || consumption < 0) return;

  return {
    display: true,
    directionStyle: style.backward_flow,
  };
}

function getBatteryGridFlow({ grid, batteryPower }: Measurements) {
  if (!isValidNumber(grid) || !isValidNumber(batteryPower)) return;

  if (batteryPower === 0 || grid === 0) return;

  if (batteryPower < 0 && grid < 0) return;

  if (batteryPower > 0 && grid > 0) return;

  if (grid < 0 && batteryPower > 0) {
    return {
      display: true,
      directionStyle: style.backward_flow,
    };
  }

  return {
    display: true,
    directionStyle: style.forward_flow,
  };
}

function formatMeasurement(value: number | undefined, padStart: number): string {
  if (value === undefined) return '-'.padStart(padStart, ' ');

  if (isNaN(value)) return '-'.padStart(padStart, ' ');

  if (value === 0) return '0'.padStart(padStart, ' ');
  return String(Math.abs(value)).padStart(padStart, ' ');
}
